import React from 'react';

import { AppViewMode } from 'src/constants/uiConsts';
import { FeaturesConfigType } from 'src/utils/PortalUtils/portalConfigUtils';

interface PortalAssets {
  logo: {
    logoImageUrl: string;
    iconImageUrl: string;
    faviconUrl: string;
  };
  authImage: {
    signInUrl: string;
  };
  socialShareImage: {
    imageUrl: string;
  };
}

export interface AmplifyApiSettings {
  name: string;
  endpoint: string;
  region: string;
  wssEndpoint?: string;
}

export interface CustomLabels {
  companyTerm?: string;
  companyOne?: string;
  companyMany?: string;
  clientTerm?: string;
  clientOne?: string;
  clientMany?: string;
}

export interface PortalConfigMetadata {
  title: string;
  description: string;
  linkedInUrl: string;
  facebookUrl: string;
  instagramUrl: string;
  twitterUrl: string;
  gaMeasurementId?: string;
  titleOverride?: string;
  shouldIgnoreSEOIndexing?: boolean; // if set to true, Portal will not be indexed by search engines
}

export interface OAuthConfig {
  domain: string;
  redirectSignIn: string;
  redirectSignOut: string;
  responseType: string;
  urlOpener?: (url: string, redirectUrl?: string) => Promise<unknown>;
}

export interface PortalConfig {
  /**
   * NOT the portal/workspace id. Optional and generally unset on internal
   * (dashboard) routes. For the current portal id, use `portalHeader` below.
   */
  id?: string;
  AWS: {
    Auth: {
      userPoolId: string;
      identityPoolId: string;
      region: string;
      userPoolWebClientId: string;
      oauth?: OAuthConfig;
    };
    API: {
      endpoints: AmplifyApiSettings[];
    };
    Cloudformation: {
      subdomain: string;
    };
  };
  features: FeaturesConfigType;
  MarketingSite: {
    Enabled: boolean;
    disableBadge: boolean;
    allowCallScheduling: boolean;
    callSchedulingLink: string;
  };
  name: string;
  /**
   * The current portal/workspace id, and the canonical one to use across the
   * app — this is the value sent as the `Portal` header on authenticated
   * requests (see getInitForApi). Prefer this over `id` (which is often unset).
   */
  portalHeader: string;
  subdomain: string;
  /** the view mode of the portal (internal or client) */
  viewMode: AppViewMode;
  metadata: PortalConfigMetadata;
  SignoutRedirectURL: string;
  BaseAPIUrl: string;
  assets: PortalAssets;
  theme: DefaultTheme;
  brand: {
    allowClientBranding: boolean;
    clientAccentColor: string;
    clientSidebarBackgroundColor: string;
    clientSidebarTextColor: string;
    clientFontFamily: string;
  };
  clientExperienceEnabled: boolean;
  disableClientEmailNotifications: boolean;
  sendEmailsToUninvitedClients: boolean;
  isAssemblyBrandEnabled: boolean;
  /** True when this workspace was created through the Studio onboarding flow. */
  isStudioUser?: boolean;
  /**
   * Prompt captured on the Studio marketing site. Drives the post-signup landing
   * (Build tab vs. CRM) and seeds the app builder.
   */
  initialPrompt?: string;
  mfaPreferences: {
    requireClientMfa: boolean;
    requireInternalMfa: boolean;
  };
  authPreferences: {
    enableMagicLinks: boolean;
  };
  updatePortalConfig?: (config: {
    id: string;
    sendEmailsToUninvitedClients: boolean;
    mfaPreferences: {
      requireClientMfa: boolean;
      requireInternalMfa: boolean;
    };
    clientExperienceEnabled: boolean;
    clientInvitesDisabled: boolean;
    disableClientEmailNotifications: boolean;
    disableCompanies: boolean;
    disableGoogleSignIn: boolean;
    gaMeasurementId: string;
    signupDisabled: boolean;
    signoutRedirectUrl: string;
    authPreferences: {
      enableMagicLinks: boolean;
    };
    customLabels: CustomLabels;
  }) => void; // optional because it only gets set in App.tsx
  portalUrl: string;
  /** Raw STREAM_SERVICE_API_URL before any core-API fallback. Empty string means unconfigured. */
  streamServiceApiUrl?: string;
}

export interface DefaultTheme {
  palette: {
    primary: {
      main: string;
      contrastText: string;
    };
    secondary: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
    error: {
      contrastText: string;
      dark: string;
      main: string;
      light: string;
    };
    text: {
      primary: string;
      secondary: string;
      link: string;
    };
    link: string;
    icon: string;
    background: {
      default: string;
      paper: string;
    };
    divider: string;
  };
}

export const defaultTheme: DefaultTheme = {
  palette: {
    primary: {
      main: '#000000',
      contrastText: '#fff',
    },
    secondary: {
      light: '#fff',
      main: '#fff',
      dark: '#ccc',
      contrastText: '#000',
    },
    error: {
      contrastText: '#fff',
      dark: '#b71c1c',
      main: '#e53935',
      light: '#ef5350',
    },
    text: {
      primary: '#263238',
      secondary: '#546e7a',
      link: '#1e88e5',
    },
    link: '#1565c0',
    icon: '#546e7a',
    background: {
      default: '#F4F6F8',
      paper: '#fff',
    },
    divider: '#eee',
  },
};

export const initialConfig: PortalConfig = {
  AWS: {
    Auth: {
      identityPoolId: 'us-east-1:57697fef-cff2-4891-b23b-f5b5d123063f',
      region: 'us-east-1',
      userPoolId: 'us-east-1_68WePm9cv',
      userPoolWebClientId: '75hbpr88qif85pqqdqmrkl185n',
    },
    API: {
      endpoints: [],
    },
    Cloudformation: {
      subdomain: '',
    },
  },
  features: {
    customLabels: {},
  },
  name: '',
  portalHeader: '',
  subdomain: '',
  viewMode: AppViewMode.INTERNAL,
  metadata: {
    title: '',
    description: '',
    gaMeasurementId: '',
    facebookUrl: '',
    instagramUrl: '',
    linkedInUrl: '',
    twitterUrl: '',
    shouldIgnoreSEOIndexing: false,
  },
  MarketingSite: {
    Enabled: false,
    disableBadge: false,
    allowCallScheduling: false,
    callSchedulingLink: '',
  },
  SignoutRedirectURL: '',
  BaseAPIUrl: '',
  assets: {
    logo: {
      logoImageUrl: '',
      iconImageUrl: '',
      faviconUrl: '',
    },
    authImage: {
      signInUrl: '',
    },
    socialShareImage: {
      imageUrl: '',
    },
  },
  theme: defaultTheme,
  brand: {
    allowClientBranding: false,
    clientAccentColor: '',
    clientSidebarTextColor: '',
    clientSidebarBackgroundColor: '',
    clientFontFamily: '',
  },
  clientExperienceEnabled: false,
  sendEmailsToUninvitedClients: false,
  disableClientEmailNotifications: false,
  mfaPreferences: {
    requireClientMfa: false,
    requireInternalMfa: false,
  },
  authPreferences: {
    enableMagicLinks: false,
  },
  isAssemblyBrandEnabled: false,
  portalUrl: '',
  streamServiceApiUrl: '',
};

export const PortalConfigContext = React.createContext(initialConfig);
